import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, RefreshCcw, Check, Plus, Smile, Flag, Clock, BarChart3, Loader2 } from 'lucide-react';
import EmojiPicker from './EmojiPicker.tsx'; // Assuming this is your updated EmojiPicker
import { DEFAULT_PLACEHOLDER_IMAGE } from './config.js';
import { IShout, IFormatting, IPollOption } from './EchoVoid.tsx';

const formatTimeAgo = (iso: string): string => {
    try {
        const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
        if (seconds < 5) return 'just now';
        if (seconds < 60) return `${seconds}s ago`;
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) return `${minutes}m ago`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `${hours}h ago`;
        const days = Math.floor(hours / 24);
        return `${days}d ago`;
    } catch (e) {
        console.error("Error formatting time:", e, "Input:", iso);
        return "a while ago";
    }
};

const parseFormattedTextLocal = (html: string | null): string => { return html || ''; };

interface ShoutModalProps {
    shout: IShout | null;
    onClose: () => void;
    onEcho: (shoutId: string) => void;
    onFlag: (event: React.MouseEvent<HTMLButtonElement>, shoutId: string) => void;
    onReact: (shoutId: string, emoji: string, add: boolean) => void;
    getThemeColors: () => { primary: string; secondary: string; hover: string; color: string };
    echoedShoutIds: Set<string>;
    flaggedShoutIds: Set<string>;
    flagButtonRef: React.RefObject<HTMLButtonElement>;
    initialUserReactions?: Set<string>;
    onVoteOnPoll: (shoutId: string, optionId: string) => Promise<IShout | void>;
    userVotedOptionId?: string | null;
}

// Define the number emojis array outside the component or at the top level of the component function
const NUMBER_EMOJIS = ['1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣', '6️⃣', '7️⃣', '8️⃣', '9️⃣'];

const ShoutModal: React.FC<ShoutModalProps> = ({
    shout,
    onClose,
    onEcho,
    onFlag,
    onReact,
    getThemeColors,
    echoedShoutIds,
    flaggedShoutIds,
    flagButtonRef,
    initialUserReactions = new Set(),
    onVoteOnPoll,
    userVotedOptionId
}) => {
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [emojiPickerPosition, setEmojiPickerPosition] = useState({ top: 0, left: 0 });
    const [currentUserReactions, setCurrentUserReactions] = useState<Set<string>>(initialUserReactions);
    const [votingForOptionId, setVotingForOptionId] = useState<string | null>(null);
    const [localVotedOptionId, setLocalVotedOptionId] = useState<string | null>(null);

    useEffect(() => {
        setCurrentUserReactions(initialUserReactions);
        setVotingForOptionId(null);
        setLocalVotedOptionId(userVotedOptionId || null);
    }, [shout, initialUserReactions, userVotedOptionId]);

    if (!shout) return null;

    const theme = getThemeColors();

    const handleAddReactionClick = () => {
        const pickerWidth = 320;
        const pickerHeight = 400;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const left = Math.max(10, (vw - pickerWidth) / 2);
        const top = Math.max(10, (vh - pickerHeight) / 2);
        setEmojiPickerPosition({ top, left });
        setShowEmojiPicker(true);
    };

    const handleSelectEmoji = (emoji: string) => {
        if (!shout) return;
        const alreadyReacted = currentUserReactions.has(emoji);
        onReact(shout.id, emoji, !alreadyReacted);
        setCurrentUserReactions(prev => {
            const newSet = new Set(prev);
            if (alreadyReacted) newSet.delete(emoji);
            else newSet.add(emoji);
            return newSet;
        });
        setShowEmojiPicker(false);
    };

    const handleReactionBubbleClick = (emoji: string) => {
        if (!shout) return;
        const alreadyReacted = currentUserReactions.has(emoji);
        onReact(shout.id, emoji, !alreadyReacted);
        setCurrentUserReactions(prev => {
            const newSet = new Set(prev);
            if (alreadyReacted) newSet.delete(emoji);
            else newSet.add(emoji);
            return newSet;
        });
    };

    const displayedReactions = shout.reactions_json ? Object.entries(shout.reactions_json)
        .filter(([, count]) => count > 0)
        .sort(([, countA], [, countB]) => countB - countA) : [];

    const hasUserVoted = !!localVotedOptionId;

    const handleVote = async (optionId: string) => {
        if (!shout || hasUserVoted || votingForOptionId) return;
        setVotingForOptionId(optionId);
        try {
            const updatedShout = await onVoteOnPoll(shout.id, optionId);
            if (updatedShout) {
                setLocalVotedOptionId(optionId);
            }
        } catch (error) {
            console.error("Voting failed:", error);
        } finally {
            setVotingForOptionId(null);
        }
    };

    const getPollPercentage = (votes: number, totalVotes: number | null): number => {
        if (totalVotes === null || totalVotes === 0) return 0;
        return Math.round((votes / totalVotes) * 100);
    };

    return (
        <>
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
                <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto flex flex-col shadow-xl">
                    <div className="p-4 border-b border-gray-700 flex justify-between items-start sticky top-0 bg-gray-800 z-10 rounded-t-lg">
                        <h2 className="text-xl font-bold pr-8 text-white">{shout.headline}</h2>
                        <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors"> <X size={24} /> </button>
                    </div>

                    <div className="p-6 flex-grow overflow-y-auto custom-scrollbar-modal-body">
                        {shout.content_type === 'text' ? (
                            <div className="prose prose-invert max-w-none break-words text-white shout-content-display" style={{ textAlign: shout.formatting?.alignment || 'left', color: shout.formatting?.textColor || 'white' }} dangerouslySetInnerHTML={{ __html: parseFormattedTextLocal(shout.text_content) }} />
                        ) : shout.content_type === 'video' ? (
                            <div className="aspect-video w-full">
                                <iframe key={shout.video_link} src={shout.video_link || ''} className="w-full h-full border-0 rounded" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title="Embedded video"></iframe>
                            </div>
                        ) : shout.content_type === 'image' ? (
                            <img src={shout.image_url || DEFAULT_PLACEHOLDER_IMAGE} alt="Shout content" className="w-full rounded" onError={(e) => ((e.target as HTMLImageElement).src = DEFAULT_PLACEHOLDER_IMAGE)} />
                        ) : shout.content_type === 'poll' && shout.poll_options ? (
                            <div className="space-y-4">
                                <div className="flex items-center gap-2 mb-1 text-purple-400">
                                    <BarChart3 size={18} />
                                    <span className="text-sm font-medium">Poll</span>
                                    {hasUserVoted && (
                                        <span className="text-xs text-gray-400">• You voted</span>
                                    )}
                                     {shout.total_votes !== null && (
                                        <span className="text-xs text-gray-400">• Total votes: {shout.total_votes.toLocaleString()}</span>
                                    )}
                                </div>
                                <div className="space-y-3">
                                    {shout.poll_options.map((option, index) => { // Added index here
                                        const percentage = getPollPercentage(option.votes, shout.total_votes);
                                        const isSelectedAndVoted = localVotedOptionId === option.id;
                                        const isCurrentlyVotingForThis = votingForOptionId === option.id;
                                        
                                        // MODIFIED LOGIC FOR DEFAULT EMOJI
                                        const displayEmoji = option.emoji || NUMBER_EMOJIS[index] || '❓';

                                        return (
                                            <div key={option.id} className="relative overflow-hidden">
                                                <button
                                                    onClick={() => handleVote(option.id)}
                                                    disabled={hasUserVoted || !!votingForOptionId}
                                                    className={`w-full p-3.5 rounded-lg border-2 transition-all duration-300 text-left
                                                        ${hasUserVoted || votingForOptionId ? 'cursor-default' : 'hover:border-purple-500 cursor-pointer'}
                                                        ${isCurrentlyVotingForThis ? 'border-purple-600 bg-purple-600/20' 
                                                            : isSelectedAndVoted ? 'border-purple-500 bg-purple-600/30' 
                                                            : hasUserVoted ? 'border-gray-600 bg-gray-700/30' 
                                                            : 'border-gray-600 bg-gray-700/50 hover:bg-gray-700/70'
                                                        }`}
                                                >
                                                    {(hasUserVoted || isCurrentlyVotingForThis) && (
                                                        <div
                                                            className={`absolute top-0 left-0 bottom-0 rounded-md transition-all duration-500 ease-out
                                                                ${isSelectedAndVoted || isCurrentlyVotingForThis ? 'bg-purple-600/40' : 'bg-gray-600/20'}`}
                                                            style={{ width: `${percentage}%`, maxWidth: '100%' }}
                                                        />
                                                    )}
                                                    
                                                    <div className="relative flex items-center justify-between z-10">
                                                        <div className="flex items-center gap-3">
                                                            {/* Use the modified displayEmoji here */}
                                                            <span className="text-2xl">{displayEmoji}</span>
                                                            <span className={`font-medium ${isSelectedAndVoted || isCurrentlyVotingForThis ? 'text-purple-100' : 'text-white'}`}>{option.text}</span>
                                                            {isCurrentlyVotingForThis && <Loader2 size={18} className="animate-spin text-purple-300" />}
                                                        </div>
                                                        {(hasUserVoted || isCurrentlyVotingForThis) && (
                                                            <div className="flex items-center gap-3 text-sm">
                                                                <span className={`${isSelectedAndVoted || isCurrentlyVotingForThis ? 'text-purple-200' : 'text-gray-300'}`}>{option.votes}</span>
                                                                <span className={`${isSelectedAndVoted || isCurrentlyVotingForThis ? 'text-purple-300' :theme.secondary} font-bold`}>{percentage}%</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : (
                            <p className="text-gray-400">Content type not recognized or data missing.</p>
                        )}
                    </div>

                    {shout.content_type !== 'poll' && displayedReactions.length > 0 && (
                         <div className="px-6 py-3 bg-gray-800/50">
                            <div className={`flex flex-wrap gap-1.5 items-center ${displayedReactions.length > 10 ? 'max-h-20 overflow-y-auto custom-scrollbar-reactions pr-1' : ''}`}>
                                {displayedReactions.map(([emoji, count]) => (
                                    <button
                                        key={emoji}
                                        onClick={() => handleReactionBubbleClick(emoji)}
                                        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-sm transition-all transform hover:scale-105 ${
                                            currentUserReactions.has(emoji)
                                                ? 'bg-purple-600/40 border border-purple-500/60 text-purple-200 shadow-md'
                                                : 'bg-gray-700/70 hover:bg-gray-600/90 text-gray-300 border border-transparent hover:border-gray-500/50'
                                        }`}
                                        title={`React with ${emoji}`}
                                    >
                                        <span className="text-base">{emoji}</span>
                                        <span className="text-xs font-medium">{count}</span>
                                    </button>
                                ))}
                                <button
                                    onClick={handleAddReactionClick}
                                    className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-gray-700/70 hover:bg-gray-600/90 text-gray-300 hover:text-white transition-all transform hover:scale-105 border border-transparent hover:border-gray-500/50"
                                    title="Add reaction"
                                >
                                    <Plus size={18} />
                                </button>
                            </div>
                        </div>
                    )}
                     {shout.content_type === 'poll' && displayedReactions.length > 0 && (
                         <div className="px-6 py-3 bg-gray-800/50">
                            <div className={`flex flex-wrap gap-1.5 items-center ${displayedReactions.length > 10 ? 'max-h-20 overflow-y-auto custom-scrollbar-reactions pr-1' : ''}`}>
                                {displayedReactions.map(([emoji, count]) => (
                                    <button
                                        key={emoji}
                                        onClick={() => handleReactionBubbleClick(emoji)}
                                        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-sm transition-all transform hover:scale-105 ${
                                            currentUserReactions.has(emoji)
                                                ? 'bg-purple-600/40 border border-purple-500/60 text-purple-200 shadow-md'
                                                : 'bg-gray-700/70 hover:bg-gray-600/90 text-gray-300 border border-transparent hover:border-gray-500/50'
                                        }`}
                                        title={`React with ${emoji}`}
                                    >
                                        <span className="text-base">{emoji}</span>
                                        <span className="text-xs font-medium">{count}</span>
                                    </button>
                                ))}
                                <button
                                    onClick={handleAddReactionClick}
                                    className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-gray-700/70 hover:bg-gray-600/90 text-gray-300 hover:text-white transition-all transform hover:scale-105 border border-transparent hover:border-gray-500/50"
                                    title="Add reaction"
                                >
                                    <Plus size={18} />
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="px-6 py-3 sticky bottom-0 bg-gray-800 z-10 rounded-b-lg">
                        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 text-sm">
                            <div className="flex items-center gap-x-3 text-gray-400">
                                <span>{formatTimeAgo(shout.created_at)}</span>
                                <div className="flex items-center gap-1" title="Time Remaining">
                                    <Clock size={14} className="text-yellow-400" />
                                    <span>{shout.secondsRemaining ? Math.floor(shout.secondsRemaining) : '0'}s</span>
                                </div>
                                <div className="flex items-center gap-1" title={`Echoes: ${shout.echoes}`}>
                                    <RefreshCcw size={14} className={shout.echoes > 0 ? theme.secondary : 'text-gray-400'} />
                                    <span>{shout.echoes}</span>
                                </div>
                                <div className="relative">
                                    {flaggedShoutIds.has(shout.id) ? (
                                        <span className="flex items-center cursor-default" title="You have flagged this post">
                                            <Flag size={16} className="text-gray-500" />
                                            <span className="ml-1 text-xs text-gray-500">(Flagged)</span>
                                        </span>
                                    ) : (
                                        <button
                                            ref={flagButtonRef}
                                            onClick={(e) => onFlag(e, shout.id)}
                                            className="text-red-500 hover:text-red-400 transition-colors flex items-center" title="Flag this post">
                                            <Flag size={16} />
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center gap-x-3">
                                {displayedReactions.length === 0 && (
                                    <button
                                        onClick={handleAddReactionClick}
                                        className="bg-gray-700/60 hover:bg-gray-600/80 px-3 py-2 rounded-full transition-colors flex items-center gap-1 text-gray-300 hover:text-white"
                                        title="Add reaction"
                                        style={{ height: '2.5rem' }}
                                    >
                                        <Smile size={14} />
                                        <Plus size={12} />
                                    </button>
                                )}
                                {echoedShoutIds.has(shout.id) ? (
                                    <div className={`${theme.primary} bg-opacity-50 px-4 py-2 rounded-full font-medium flex items-center text-white cursor-default text-sm`} style={{ height: '2.5rem' }}>
                                        <Check size={16} className="mr-2" /> Echoed
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => onEcho(shout.id)}
                                        className={`${theme.primary} ${theme.hover} px-4 py-2 rounded-full font-medium flex items-center text-white transition-colors duration-150 text-sm`}
                                        style={{ height: '2.5rem' }}
                                    >
                                        <RefreshCcw size={16} className="mr-2" />
                                        Echo ({shout.echoes})
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {createPortal(
                <EmojiPicker
                    isOpen={showEmojiPicker}
                    onClose={() => setShowEmojiPicker(false)}
                    onSelectEmoji={handleSelectEmoji}
                    position={emojiPickerPosition}
                    userReactions={currentUserReactions}
                />,
                document.body
            )}
            <style jsx>{`
                .custom-scrollbar-reactions::-webkit-scrollbar { width: 4px; }
                .custom-scrollbar-reactions::-webkit-scrollbar-track { background: rgba(0,0,0,0.1); border-radius: 2px; }
                .custom-scrollbar-reactions::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 2px; }
                .custom-scrollbar-reactions::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.2); }
                
                .custom-scrollbar-modal-body::-webkit-scrollbar { width: 6px; }
                .custom-scrollbar-modal-body::-webkit-scrollbar-track { background: rgba(0,0,0,0.1); border-radius: 3px; }
                .custom-scrollbar-modal-body::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 3px; }
                .custom-scrollbar-modal-body::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.25); }

                .shout-content-display strong { color: inherit !important; }
            `}</style>
        </>
    );
};

export default ShoutModal;
